// July 2026 revision: 12 observed roles (was 20), 3 per category. Renamed
// roles keep their original IDs so historical tags carry forward:
// drill_sergeant → "Facilitator", columbo → "Inspector" (moved Right Chemistry
// → Compelling Content; safe because observedRoles is stored as a flat id map).
// The removed Team Dynamics personality types were absorbed into these roles.
// Full old→new mapping: docs/question-reduction-worksheet.md.
export const ROLES_BY_CATEGORY = {
  compellingContent: [
    {
      id: 'columbo',
      name: 'Inspector',
      description: 'Asks lots of questions out of curiosity.',
    },
    {
      id: 'storyteller',
      name: 'Storyteller',
      description: 'Goes into great detail on examples and stories.',
    },
    {
      id: 'bulldozer',
      name: 'Bulldozer',
      description: 'Pushes their viewpoint forcefully and dominates airtime.',
    },
  ],
  healthyBehaviors: [
    {
      id: 'harmonizer',
      name: 'Harmonizer',
      description: 'Often steps in when two or more team members are in conflict.',
    },
    {
      id: 'joker',
      name: 'Joker',
      description: 'Uses humor to lighten the mood or deflect from sensitive topics.',
    },
    {
      id: 'spectator',
      name: 'Spectator',
      description: 'Physically present but mostly disengaged from the conversation.',
    },
  ],
  rightGenius: [
    {
      id: 'innovator',
      name: 'Innovator',
      description: 'Helps uncover problems, opportunities, and new ideas.',
    },
    {
      id: 'activator',
      name: 'Activator',
      description: 'Able to determine the viability of solutions and get buy-in.',
    },
    {
      id: 'enabler',
      name: 'Implementor',
      description: 'Helps move things along practically and pushes for results.',
    },
  ],
  simpleProcess: [
    {
      id: 'drill_sergeant',
      name: 'Facilitator',
      description: 'Protects and promotes discipline in the meeting.',
    },
    {
      id: 'referee',
      name: 'Referee',
      description: 'Often reminds the team when off track or breaking rules.',
    },
    {
      id: 'closer',
      name: 'Closer',
      description: 'Pushes the group toward decisions and action.',
    },
  ],
};

// Roles retired in the July 2026 revision. Never shown on new assessments or
// inventories, but old responses/meetings may still reference these IDs in
// observedRoles — keep them here so historical data renders.
export const RETIRED_ROLES = {
  taskmaster: {
    category: 'simpleProcess',
    name: 'Taskmaster',
    description: 'Identifies next steps, responsibilities, and practical implications.',
  },
  historian: {
    category: 'simpleProcess',
    name: 'Historian',
    description: 'Reminds the team of past decisions, experiences, and lessons learned.',
  },
  yoda: {
    category: 'compellingContent',
    name: 'Yoda',
    description: 'Often the first to sense that something isn\'t quite right or that an opportunity may exist by raising issues others might overlook.',
  },
  inventor: {
    category: 'compellingContent',
    name: 'Inventor',
    description: 'Produces new ideas and creative approaches that energize the conversation and expand the team\'s thinking.',
  },
  catalyst: {
    category: 'compellingContent',
    name: 'Catalyst',
    description: 'Injects energy and new thinking into the conversation. Helps the group move beyond stale assumptions.',
  },
  skeptic: {
    category: 'healthyBehaviors',
    name: 'Skeptic',
    description: 'Challenges optimism and tests assumptions, helping prevent groupthink.',
  },
  multitasker: {
    category: 'healthyBehaviors',
    name: 'Multitasker',
    description: 'Appears present but is frequently distracted by devices or side work.',
  },
  socrates: {
    category: 'rightGenius',
    name: 'Socrates',
    description: 'Poses deeper or philosophical questions that push the team to think more critically (Wonder).',
  },
  analyzer: {
    category: 'rightGenius',
    name: 'Analyzer',
    description: 'Examines ideas critically, testing whether they make sense and identifying potential flaws or risks — but can also get buried in data and complexity (Discernment).',
  },
  translator: {
    category: 'rightGenius',
    name: 'Translator',
    description: 'Distills conversation into clear statements about what the team is actually deciding to help with buy-in (Galvanizing).',
  },
  synthesizer: {
    category: 'rightGenius',
    name: 'Synthesizer',
    description: 'Listens carefully and summarizes the conversation in a way that helps everyone understand what needs to be done next (Enablement/Tenacity).',
  },
};

export const ALL_ROLES_BY_ID = {
  ...Object.fromEntries(
    Object.values(ROLES_BY_CATEGORY).flat().map(r => [r.id, r])
  ),
  ...Object.fromEntries(
    Object.entries(RETIRED_ROLES).map(([id, r]) => [id, { id, ...r }])
  ),
};

// Roles to render for a category when displaying stored observedRoles data:
// the current roles plus any retired roles of that category the data references.
export function rolesForDisplay(categoryKey, observedRoles = {}) {
  const active = ROLES_BY_CATEGORY[categoryKey] || [];
  const retired = Object.entries(RETIRED_ROLES)
    .filter(([id, r]) =>
      r.category === categoryKey &&
      Object.prototype.hasOwnProperty.call(observedRoles, id)
    )
    .map(([id, r]) => ({ id, ...r }));
  return [...active, ...retired];
}
